import React, { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel.jsx";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import CustomDateTimePicker from "../../components/DateTimePicker/dateTimePicker.js";
import moment from "moment";
import "./manualBilling.scss";

const MODAL_Z_INVOICE_DETAILS = 10450;
const MODAL_Z_APPROVE_PROOF = 10550;

const PAYMENT_METHODS = [
  { value: "Card", label: "Card" },
  { value: "Cash", label: "Cash" },
  { value: "Bank Transfer", label: "Bank Transfer" },
  { value: "Other", label: "Other" },
];

const apiBaseUrl =
  process.env.REACT_APP_Environment === "development"
    ? process.env.REACT_APP_API_URL
    : process.env.REACT_APP_API_URL_Production;

const ManualBilling = ({ CompID, currency = "USD", parentComID = 0 }) => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceTransactions, setInvoiceTransactions] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [proofFile, setProofFile] = useState(null);

  const [createForm, setCreateForm] = useState({
    itemDescription: "ALTMan POS subscription",
    totalAmount: "",
    servicePeriodStart: moment().format("YYYY-MM-DD"),
    servicePeriodEnd: moment().add(6, "months").format("YYYY-MM-DD"),
    dueDate: moment().add(7, "days").format("YYYY-MM-DD"),
    notes: "",
  });

  const [editForm, setEditForm] = useState({
    invoiceDate: "",
    dueDate: "",
    servicePeriodStart: "",
    servicePeriodEnd: "",
    notes: "",
  });

  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    paymentMethod: "Card",
    paymentReference: "",
    notes: "",
  });

  const isParentCompany = !parentComID || parentComID === 0;

  const fetchInvoices = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_INVOICES_BY_COMPANY_ID, {
        params: { compID: CompID },
      });
      if (response.data.status && Array.isArray(response.data.data)) {
        const invoiceData = response.data.data;
        const enriched = await Promise.all(
          invoiceData.map(async (inv) => {
            try {
              const txnRes = await axiosInstance.get(APIs.GET_TRANSACTIONS_BY_INVOICE, {
                params: { invoiceID: inv.invoiceID },
              });
              return {
                ...inv,
                _transactions:
                  txnRes.data.status && Array.isArray(txnRes.data.data) ? txnRes.data.data : [],
              };
            } catch {
              return { ...inv, _transactions: [] };
            }
          })
        );
        setInvoices(enriched);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      showErrorToast("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  const getReservedPendingAmount = (transactions) => {
    if (!Array.isArray(transactions)) return 0;
    return transactions
      .filter((t) => (t.status || "").trim().toLowerCase() === "pending")
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
  };

  const getBalanceDue = (invoice, transactions) => {
    if (!invoice) return 0;
    const gross = Math.max(
      0,
      parseFloat(invoice.totalAmount) - parseFloat(invoice.paidAmount || 0)
    );
    const txns = transactions ?? invoice._transactions ?? [];
    return Math.max(0, gross - getReservedPendingAmount(txns));
  };

  const isInvoicePayable = (invoice, transactions) => {
    if (!invoice) return false;
    if (invoice.status === "Cancelled") return false;
    if (invoice.paymentStatus === "Paid") return false;
    return getBalanceDue(invoice, transactions) > 0;
  };

  const isInvoiceDeletable = (invoice) => {
    if (!invoice) return false;
    if (invoice.status === "Cancelled") return false;
    if (invoice.paymentStatus === "Paid") return false;
    const paid = parseFloat(invoice.paidAmount || 0);
    if (paid > 0) return false;
    const txns = invoice._transactions ?? [];
    return !txns.some((t) => (t.status || "").trim().toLowerCase() === "success");
  };

  const openEditInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setEditForm({
      invoiceDate: invoice.invoiceDate
        ? moment(invoice.invoiceDate).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD"),
      dueDate: invoice.dueDate
        ? moment(invoice.dueDate).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD"),
      servicePeriodStart: invoice.servicePeriodStart
        ? moment(invoice.servicePeriodStart).format("YYYY-MM-DD")
        : "",
      servicePeriodEnd: invoice.servicePeriodEnd
        ? moment(invoice.servicePeriodEnd).format("YYYY-MM-DD")
        : "",
      notes: invoice.notes || "",
    });
    setShowEditModal(true);
  };

  const handleEditInvoice = async () => {
    if (!selectedInvoice) return;
    if (
      editForm.servicePeriodStart &&
      editForm.servicePeriodEnd &&
      moment(editForm.servicePeriodEnd).isSameOrBefore(moment(editForm.servicePeriodStart))
    ) {
      showErrorToast("Service period end must be after start");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        invoiceID: selectedInvoice.invoiceID,
        invoiceDate: moment(editForm.invoiceDate).format("YYYY-MM-DDTHH:mm:ss"),
        dueDate: moment(editForm.dueDate).format("YYYY-MM-DDTHH:mm:ss"),
        servicePeriodStart: editForm.servicePeriodStart
          ? moment(editForm.servicePeriodStart).format("YYYY-MM-DDTHH:mm:ss")
          : null,
        servicePeriodEnd: editForm.servicePeriodEnd
          ? moment(editForm.servicePeriodEnd).format("YYYY-MM-DDTHH:mm:ss")
          : null,
        notes: editForm.notes,
      };

      const response = await axiosInstance.put(APIs.PUT_UPDATE_INVOICE, payload);
      if (response.data.status) {
        showSuccessToast("Invoice updated successfully");
        setShowEditModal(false);
        fetchInvoices();
        if (showDetails && selectedInvoice?.invoiceID) {
          fetchInvoiceDetails(selectedInvoice.invoiceID, true);
        }
      } else {
        showErrorToast(response.data.message || "Failed to update invoice");
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to update invoice");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    if (!isInvoiceDeletable(invoice)) {
      showErrorToast("Only unpaid invoices with no confirmed payments can be deleted");
      return;
    }
    if (
      !window.confirm(
        `Delete invoice ${invoice.invoiceNumber}? This cannot be undone. Pending payment entries will also be removed.`
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_INVOICE, {
        params: { invoiceID: invoice.invoiceID },
      });
      if (response.data.status) {
        showSuccessToast("Invoice deleted successfully");
        if (showDetails && selectedInvoice?.invoiceID === invoice.invoiceID) {
          closeDetails();
        }
        fetchInvoices();
      } else {
        showErrorToast(response.data.message || "Failed to delete invoice");
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to delete invoice");
    } finally {
      setLoading(false);
    }
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedInvoice(null);
    setInvoiceTransactions([]);
  };

  const fetchInvoiceDetails = async (invoiceID, keepModalOpen = false) => {
    try {
      setLoading(true);
      const [invoiceRes, txnRes] = await Promise.all([
        axiosInstance.get(APIs.GET_INVOICE_BY_ID, { params: { invoiceID } }),
        axiosInstance.get(APIs.GET_TRANSACTIONS_BY_INVOICE, { params: { invoiceID } }),
      ]);

      if (invoiceRes.data.status) {
        const txns =
          txnRes.data.status && Array.isArray(txnRes.data.data) ? txnRes.data.data : [];
        setSelectedInvoice({ ...invoiceRes.data.data, _transactions: txns });
        setInvoiceTransactions(txns);
        if (!keepModalOpen) setShowDetails(true);
      } else {
        showErrorToast("Failed to load invoice details");
      }
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      showErrorToast("Failed to load invoice details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (CompID && isParentCompany) {
      fetchInvoices();
    }
  }, [CompID, isParentCompany, fetchInvoices]);

  const getProofUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    const base = apiBaseUrl?.replace(/\/$/, "") || "";
    return `${base}/${path.replace(/^\//, "")}`;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      Draft: "secondary",
      Sent: "info",
      Paid: "success",
      PartiallyPaid: "warning",
      Overdue: "danger",
      Cancelled: "dark",
    };
    const color = statusMap[status] || "secondary";
    return <span className={`badge bg-${color}`}>{status}</span>;
  };

  const getPaymentStatusBadge = (status) => {
    const statusMap = {
      Pending: "warning",
      Partial: "info",
      Paid: "success",
      Refunded: "dark",
    };
    const color = statusMap[status] || "secondary";
    return <span className={`badge bg-${color}`}>{status}</span>;
  };

  const getTxnStatusBadge = (status) => {
    const map = {
      Success: "success",
      Pending: "warning",
      Failed: "danger",
      Refunded: "dark",
    };
    return <span className={`badge bg-${map[status] || "secondary"}`}>{status}</span>;
  };

  const getProofStatusBadge = (status) => {
    const statusMap = {
      None: "secondary",
      Pending: "warning",
      Approved: "success",
      Rejected: "danger",
    };
    const color = statusMap[status] || "secondary";
    return <span className={`badge bg-${color}`}>{status || "None"}</span>;
  };

  const isPendingTxn = (txn) => (txn.status || "").trim().toLowerCase() === "pending";

  const canReviewProof = (txn) => {
    const proofUrl = txnField(txn, "paymentProofUrl", "PaymentProofUrl");
    const proofStatus = (txn.proofStatus || "None").trim();
    return isPendingTxn(txn) && proofStatus === "Pending" && !!proofUrl;
  };

  const canApprovePendingPayment = (txn) => {
    if (!isPendingTxn(txn)) return false;
    const proofStatus = (txn.proofStatus || "None").trim();
    return proofStatus !== "Rejected";
  };

  const handleApprovePayment = async (txn, closeDetailsAfter = false) => {
    const txnId = txnField(txn, "transactionID", "TransactionID");
    if (!txnId) return;
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        `${APIs.POST_APPROVE_PENDING_PAYMENT}?transactionID=${txnId}`
      );
      if (response.data.status) {
        const periodEnd = response.data.data?.servicePeriodEnd
          ? moment(response.data.data.servicePeriodEnd).format("MMM DD, YYYY")
          : "";
        showSuccessToast(
          periodEnd
            ? `Payment approved. Access extended until ${periodEnd}.`
            : "Payment approved successfully."
        );
        setShowApproveModal(false);
        if (closeDetailsAfter) closeDetails();
        else if (showDetails && selectedInvoice?.invoiceID) {
          fetchInvoiceDetails(selectedInvoice.invoiceID, true);
        }
        fetchInvoices();
      } else {
        showErrorToast(response.data.message || "Failed to approve payment");
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to approve payment");
    } finally {
      setLoading(false);
    }
  };

  const renderProofCell = (txn, invoice) => {
    const proofUrl = txnField(txn, "paymentProofUrl", "PaymentProofUrl");
    const proofStatus = (txn.proofStatus || "None").trim();
    const method = txn.paymentMethod || "";
    const isBank = method === "Bank Transfer";
    const isCash = method === "Cash";
    const isPdf = proofUrl?.toLowerCase().endsWith(".pdf");

    if (!isBank && !isCash && !proofUrl && !isPendingTxn(txn)) {
      return <span className="text-muted small">—</span>;
    }

    return (
      <div className="billing-proof-actions d-flex flex-wrap align-items-center gap-2">
        {proofUrl && (
          <a
            href={getProofUrl(proofUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-primary"
            title="Open payment proof"
          >
            <i className={`fa ${isPdf ? "fa-file-pdf" : "fa-eye"} me-1`} />
            View
          </a>
        )}
        {canReviewProof(txn) && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => openApproveModal(txn, invoice)}
            title="Review proof document"
          >
            <i className="fa fa-search me-1" />
            Review proof
          </button>
        )}
        {isBank && !proofUrl && proofStatus === "None" && isPendingTxn(txn) && (
          <span className="proof-chip proof-missing">
            <i className="fa fa-exclamation-circle" /> No proof
          </span>
        )}
        {isCash && isPendingTxn(txn) && !proofUrl && (
          <span className="proof-chip proof-pending">
            <i className="fa fa-money-bill" /> Awaiting approval
          </span>
        )}
        {proofUrl && proofStatus === "Pending" && !canReviewProof(txn) && (
          <span className="proof-chip proof-pending">
            <i className="fa fa-clock" /> Awaiting approval
          </span>
        )}
        {proofStatus === "Rejected" && (
          <span className="proof-chip proof-rejected">
            <i className="fa fa-times" /> Rejected
          </span>
        )}
        {proofStatus === "Approved" && (
          <span className="proof-chip proof-approved">
            <i className="fa fa-check" /> Approved
          </span>
        )}
      </div>
    );
  };

  const openRecordPayment = (invoice) => {
    const balance = getBalanceDue(invoice);
    setSelectedInvoice(invoice);
    setPaymentForm({
      amount: balance > 0 ? balance.toFixed(2) : "",
      paymentMethod: "Card",
      paymentReference: "",
      notes: "",
    });
    setProofFile(null);
    setShowPaymentModal(true);
  };

  const openApproveModal = (txn, invoice) => {
    setSelectedTransaction(txn);
    setSelectedInvoice(invoice);
    setRejectReason("");
    setShowApproveModal(true);
  };

  const handleCreateInvoice = async () => {
    const amount = parseFloat(createForm.totalAmount);
    if (!amount || amount <= 0) {
      showErrorToast("Please enter a valid amount");
      return;
    }
    if (moment(createForm.servicePeriodEnd).isSameOrBefore(moment(createForm.servicePeriodStart))) {
      showErrorToast("Service period end must be after start");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        compID: parseInt(CompID, 10),
        itemDescription: createForm.itemDescription,
        totalAmount: amount,
        discountAmount: 0,
        taxAmount: 0,
        currency: currency || "USD",
        invoiceDate: moment().format("YYYY-MM-DDTHH:mm:ss"),
        dueDate: moment(createForm.dueDate).format("YYYY-MM-DDTHH:mm:ss"),
        servicePeriodStart: moment(createForm.servicePeriodStart).format("YYYY-MM-DDTHH:mm:ss"),
        servicePeriodEnd: moment(createForm.servicePeriodEnd).format("YYYY-MM-DDTHH:mm:ss"),
        notes: createForm.notes,
        status: "Sent",
      };

      const response = await axiosInstance.post(APIs.POST_CREATE_MANUAL_INVOICE, payload);
      if (response.data.status) {
        showSuccessToast("Invoice created successfully");
        setShowCreateModal(false);
        fetchInvoices();
      } else {
        showErrorToast(response.data.message || "Failed to create invoice");
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to create invoice");
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) {
      showErrorToast("Please enter a valid payment amount");
      return;
    }

    const balance = getBalanceDue(selectedInvoice, selectedInvoice?._transactions);
    if (amount > balance) {
      showErrorToast(
        `Payment amount cannot exceed the remaining balance of ${selectedInvoice.currency} ${balance.toFixed(2)}`
      );
      return;
    }

    const isBank = paymentForm.paymentMethod === "Bank Transfer";
    const isCash = paymentForm.paymentMethod === "Cash";

    if (isBank && !proofFile) {
      showErrorToast("Please upload payment proof for bank transfer");
      return;
    }

    try {
      setLoading(true);

      if (isBank) {
        const formData = new FormData();
        formData.append("invoiceID", String(selectedInvoice.invoiceID));
        formData.append("amount", String(amount));
        formData.append("paymentMethod", paymentForm.paymentMethod);
        formData.append("paymentReference", paymentForm.paymentReference || "");
        formData.append("notes", paymentForm.notes || "");
        formData.append("transactionDate", moment().format("YYYY-MM-DDTHH:mm:ss"));
        formData.append("proofFile", proofFile);

        const response = await axiosInstance.post(APIs.POST_RECORD_MANUAL_PAYMENT_WITH_PROOF, formData);
        if (response.data.status) {
          showSuccessToast(
            response.data.message || "Bank payment recorded. Awaiting proof approval."
          );
          setShowPaymentModal(false);
          setProofFile(null);
          fetchInvoices();
          if (showDetails && selectedInvoice?.invoiceID) {
            fetchInvoiceDetails(selectedInvoice.invoiceID, true);
          }
        } else {
          showErrorToast(response.data.message || "Failed to record bank payment");
        }
        return;
      }

      const response = await axiosInstance.post(APIs.POST_RECORD_MANUAL_PAYMENT, {
        invoiceID: selectedInvoice.invoiceID,
        amount,
        paymentMethod: paymentForm.paymentMethod,
        paymentReference: paymentForm.paymentReference,
        notes: paymentForm.notes,
        transactionDate: moment().format("YYYY-MM-DDTHH:mm:ss"),
      });

      if (!response.data.status) {
        showErrorToast(response.data.message || "Failed to record payment");
        return;
      }

      const successMessage =
        response.data.message ||
        (isCash
          ? "Cash payment recorded. Awaiting approval."
          : "Payment recorded successfully.");
      showSuccessToast(successMessage);
      setShowPaymentModal(false);
      fetchInvoices();
      if (showDetails && selectedInvoice?.invoiceID) {
        fetchInvoiceDetails(selectedInvoice.invoiceID, true);
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to record payment");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveProof = async () => {
    if (!selectedTransaction) return;
    await handleApprovePayment(selectedTransaction, true);
  };

  const handleRejectProof = async () => {
    const txnId =
      selectedTransaction?.transactionID ?? selectedTransaction?.TransactionID;
    if (!txnId) return;
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_REJECT_PAYMENT_PROOF, {
        transactionID: txnId,
        reason: rejectReason,
      });
      if (response.data.status) {
        showSuccessToast("Payment proof rejected");
        setShowApproveModal(false);
        if (showDetails && selectedInvoice) {
          fetchInvoiceDetails(selectedInvoice.invoiceID);
        }
        fetchInvoices();
      } else {
        showErrorToast(response.data.message || "Failed to reject payment");
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to reject payment");
    } finally {
      setLoading(false);
    }
  };

  const getBillingTypeBadge = (billingType) => {
    const type = (billingType || "Manual").trim();
    if (type.toLowerCase() === "auto") {
      return <span className="badge bg-info ms-1">Auto</span>;
    }
    return <span className="badge bg-secondary ms-1">Manual</span>;
  };

  const txnField = (txn, ...keys) => {
    if (!txn) return undefined;
    for (const key of keys) {
      if (txn[key] !== undefined && txn[key] !== null) return txn[key];
    }
    return undefined;
  };

  const getPendingPaymentTransaction = (invoice) => {
    if (!invoice._transactions) return null;
    return invoice._transactions.find((t) => isPendingTxn(t));
  };

  if (!isParentCompany) {
    return (
      <Panel>
        <PanelHeader>Billing</PanelHeader>
        <PanelBody>
          <div className="alert alert-info mb-0">
            <i className="fa fa-info-circle me-2"></i>
            Billing is managed on the main branch company only.
          </div>
        </PanelBody>
      </Panel>
    );
  }

  if (loading && invoices.length === 0 && !showCreateModal) {
    return <Preloader />;
  }

  const balanceDue = selectedInvoice
    ? getBalanceDue(selectedInvoice, invoiceTransactions)
    : 0;

  return (
    <div className="manual-billing-page">
      <CustomModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Manual Invoice"
        onSubmit={handleCreateInvoice}
        submitButtonText="Create Invoice"
        showButton={true}
        modalSize="modal-lg"
        maxHeight="90vh"
        isCentered
      >
        <div className="row g-3">
          <div className="col-12">
            <FormInput
              label="Description"
              type="text"
              value={createForm.itemDescription}
              onchange={(e) => setCreateForm({ ...createForm, itemDescription: e.target.value })}
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label={`Amount (${currency})`}
              type="number"
              value={createForm.totalAmount}
              onchange={(e) => setCreateForm({ ...createForm, totalAmount: e.target.value })}
            />
          </div>
          <div className="col-md-6">
            <CustomDateTimePicker
              label="Due Date"
              value={createForm.dueDate}
              onChange={(v) => setCreateForm({ ...createForm, dueDate: v })}
              mode="date"
            />
          </div>
          <div className="col-md-6">
            <CustomDateTimePicker
              label="Service Period Start"
              value={createForm.servicePeriodStart}
              onChange={(v) => setCreateForm({ ...createForm, servicePeriodStart: v })}
              mode="date"
            />
          </div>
          <div className="col-md-6">
            <CustomDateTimePicker
              label="Service Period End"
              value={createForm.servicePeriodEnd}
              onChange={(v) => setCreateForm({ ...createForm, servicePeriodEnd: v })}
              mode="date"
            />
          </div>
          <div className="col-12">
            <label className="form-label">Notes</label>
            <textarea
              className="form-control"
              rows={2}
              value={createForm.notes}
              onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
            />
          </div>
        </div>
      </CustomModal>

      <CustomModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={`Edit Invoice - ${selectedInvoice?.invoiceNumber || ""}`}
        onSubmit={handleEditInvoice}
        submitButtonText="Save Changes"
        showButton={true}
        modalSize="modal-lg"
        maxHeight="90vh"
        isCentered
      >
        <div className="row g-3">
          <div className="col-md-6">
            <CustomDateTimePicker
              label="Invoice Date"
              value={editForm.invoiceDate}
              onChange={(v) => setEditForm({ ...editForm, invoiceDate: v })}
              mode="date"
            />
          </div>
          <div className="col-md-6">
            <CustomDateTimePicker
              label="Due Date"
              value={editForm.dueDate}
              onChange={(v) => setEditForm({ ...editForm, dueDate: v })}
              mode="date"
            />
          </div>
          <div className="col-md-6">
            <CustomDateTimePicker
              label="Service Period Start"
              value={editForm.servicePeriodStart}
              onChange={(v) => setEditForm({ ...editForm, servicePeriodStart: v })}
              mode="date"
            />
          </div>
          <div className="col-md-6">
            <CustomDateTimePicker
              label="Service Period End"
              value={editForm.servicePeriodEnd}
              onChange={(v) => setEditForm({ ...editForm, servicePeriodEnd: v })}
              mode="date"
            />
          </div>
          <div className="col-12">
            <label className="form-label">Notes</label>
            <textarea
              className="form-control"
              rows={2}
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
            />
          </div>
        </div>
      </CustomModal>

      <CustomModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title={`Record Payment - ${selectedInvoice?.invoiceNumber || ""}`}
        onSubmit={handleRecordPayment}
        submitButtonText="Record Payment"
        showButton={true}
        modalSize="modal-lg"
        maxHeight="90vh"
        isCentered
      >
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Payment Method</label>
            <select
              className="form-select"
              value={paymentForm.paymentMethod}
              onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <FormInput
              label={`Amount (${currency})`}
              type="number"
              value={paymentForm.amount}
              onchange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
            />
            {selectedInvoice && (
              <small className="text-muted">
                Maximum: {selectedInvoice.currency}{" "}
                {getBalanceDue(selectedInvoice, selectedInvoice._transactions).toFixed(2)}
              </small>
            )}
          </div>
          <div className="col-md-6">
            <FormInput
              label="Reference / Transaction ID"
              type="text"
              value={paymentForm.paymentReference}
              onchange={(e) =>
                setPaymentForm({ ...paymentForm, paymentReference: e.target.value })
              }
            />
          </div>
          {paymentForm.paymentMethod === "Bank Transfer" && (
            <div className="col-12">
              <label className="form-label">Payment Screenshot (required)</label>
              <input
                type="file"
                className="form-control"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={(e) => setProofFile(e.target.files?.[0] || null)}
              />
              <small className="text-muted">JPG, PNG or PDF, max 5MB</small>
            </div>
          )}
          <div className="col-12">
            <label className="form-label">Notes</label>
            <textarea
              className="form-control"
              rows={2}
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
            />
          </div>
        </div>
      </CustomModal>

      {showApproveModal &&
        createPortal(
          <CustomModal
            isOpen={showApproveModal}
            onClose={() => setShowApproveModal(false)}
            title="Approve payment"
            showSubmitButton={false}
            showCancelButton={true}
            modalSize="modal-lg"
            maxHeight="90vh"
            isCentered
            zIndex={MODAL_Z_APPROVE_PROOF}
          >
        {selectedTransaction && (
          <>
            <div className="mb-3 p-3 rounded border bg-light small">
              <div>
                <strong>Method:</strong> {selectedTransaction.paymentMethod}
              </div>
              <div>
                <strong>Amount:</strong> {selectedTransaction.currency}{" "}
                {parseFloat(selectedTransaction.amount).toFixed(2)}
              </div>
              <div>
                <strong>Date:</strong>{" "}
                {moment(selectedTransaction.transactionDate).format("MMM D, YYYY")}
              </div>
            </div>
            {(() => {
              const proofUrl = txnField(
                selectedTransaction,
                "paymentProofUrl",
                "PaymentProofUrl"
              );
              if (!proofUrl) {
                const isCashPayment =
                  (selectedTransaction.paymentMethod || "").trim().toLowerCase() === "cash";
                return (
                  <div className="alert alert-warning mb-3">
                    <i className="fa fa-exclamation-triangle me-2" />
                    {isCashPayment
                      ? "No proof file was uploaded. You can approve this cash payment once you have verified receipt of funds."
                      : "No proof file was uploaded. You may still approve if you verified this payment offline (bank statement, receipt, etc.)."}
                  </div>
                );
              }
              return (
                <div className="mb-3 text-center">
                  {proofUrl.toLowerCase().endsWith(".pdf") ? (
                    <a
                      href={getProofUrl(proofUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary"
                    >
                      <i className="fa fa-file-pdf me-2" />
                      View PDF Proof
                    </a>
                  ) : (
                    <img
                      src={getProofUrl(proofUrl)}
                      alt="Payment proof"
                      style={{ maxWidth: "100%", maxHeight: "400px", borderRadius: "8px" }}
                    />
                  )}
                </div>
              );
            })()}
          </>
        )}
        <div className="mb-3">
          <label className="form-label">Rejection reason (optional)</label>
          <textarea
            className="form-control"
            rows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Required if rejecting"
          />
        </div>
        <div className="d-flex gap-2 justify-content-end flex-wrap">
          <button type="button" className="btn btn-secondary" onClick={() => setShowApproveModal(false)}>
            Cancel
          </button>
          {txnField(selectedTransaction, "paymentProofUrl", "PaymentProofUrl") && (
            <button type="button" className="btn btn-danger" onClick={handleRejectProof} disabled={loading}>
              Reject proof
            </button>
          )}
          <button type="button" className="btn btn-success" onClick={handleApproveProof} disabled={loading}>
            Approve & extend access
          </button>
        </div>
          </CustomModal>,
          document.body
        )}

      <CustomModal
        isOpen={showDetails && !!selectedInvoice}
        onClose={closeDetails}
        title={selectedInvoice ? `Invoice ${selectedInvoice.invoiceNumber}` : "Invoice"}
        showButton={true}
        showSubmitButton={false}
        showCancelButton={true}
        modalSize="modal-xl"
        maxHeight="92vh"
        isCentered
        zIndex={MODAL_Z_INVOICE_DETAILS}
      >
        {selectedInvoice && (
          <div className="billing-invoice-detail">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div className="d-flex flex-wrap gap-2">
                {getStatusBadge(selectedInvoice.status)}
                {getPaymentStatusBadge(selectedInvoice.paymentStatus)}
              </div>
              {isInvoicePayable(selectedInvoice, invoiceTransactions) && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    closeDetails();
                    openRecordPayment(selectedInvoice);
                  }}
                >
                  <i className="fa fa-money-bill me-1" />
                  Record payment
                </button>
              )}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => openEditInvoice(selectedInvoice)}
              >
                <i className="fa fa-edit me-1" />
                Edit dates
              </button>
              {isInvoiceDeletable(selectedInvoice) && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleDeleteInvoice(selectedInvoice)}
                >
                  <i className="fa fa-trash me-1" />
                  Delete
                </button>
              )}
            </div>

            <div className="billing-meta-row">
              <span className="meta-item">
                <i className="fa fa-calendar" />
                {selectedInvoice.servicePeriodStart
                  ? `${moment(selectedInvoice.servicePeriodStart).format("MMM D, YYYY")} – ${moment(selectedInvoice.servicePeriodEnd).format("MMM D, YYYY")}`
                  : "No service period"}
              </span>
              <span className="meta-item">
                <i className="fa fa-clock" />
                Due {moment(selectedInvoice.dueDate).format("MMM D, YYYY")}
              </span>
            </div>

            <div className="billing-stat-grid">
              <div className="billing-stat-card">
                <div className="stat-label">Total</div>
                <p className="stat-value">
                  {selectedInvoice.currency} {parseFloat(selectedInvoice.totalAmount).toFixed(2)}
                </p>
              </div>
              <div className="billing-stat-card stat-paid">
                <div className="stat-label">Paid</div>
                <p className="stat-value">
                  {selectedInvoice.currency}{" "}
                  {parseFloat(selectedInvoice.paidAmount || 0).toFixed(2)}
                </p>
              </div>
              <div className="billing-stat-card stat-balance">
                <div className="stat-label">Balance due</div>
                <p className="stat-value">
                  {selectedInvoice.currency} {balanceDue.toFixed(2)}
                </p>
              </div>
              <div className="billing-stat-card">
                <div className="stat-label">Subtotal</div>
                <p className="stat-value">
                  {selectedInvoice.currency}{" "}
                  {parseFloat(selectedInvoice.subtotalAmount || 0).toFixed(2)}
                </p>
              </div>
            </div>

            {selectedInvoice.notes && (
              <div className="mb-3 p-3 rounded-3 border bg-light">
                <div className="billing-section-title mb-1">Notes</div>
                <p className="mb-0 text-muted">{selectedInvoice.notes}</p>
              </div>
            )}

            {selectedInvoice.items?.length > 0 && (
              <div className="mb-4">
                <div className="billing-section-title">Line items</div>
                <div className="billing-table-wrap">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.items.map((item) => (
                        <tr key={item.invoiceItemID}>
                          <td>{item.itemDescription}</td>
                          <td className="text-end fw-semibold">
                            {selectedInvoice.currency}{" "}
                            {parseFloat(item.totalPrice).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="billing-section-title">Payment history</div>
            {invoiceTransactions.length === 0 ? (
              <p className="text-muted small mb-0">No payments recorded yet.</p>
            ) : (
              <div className="billing-table-wrap">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Method</th>
                      <th className="text-end">Amount</th>
                      <th>Status</th>
                      <th>Proof</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceTransactions.map((txn) => (
                      <tr key={txn.transactionID ?? txn.TransactionID}>
                        <td className="text-nowrap">
                          {moment(txn.transactionDate).format("MMM D, YYYY")}
                        </td>
                        <td>{txn.paymentMethod}</td>
                        <td className="text-end fw-semibold">
                          {txn.currency} {parseFloat(txn.amount).toFixed(2)}
                        </td>
                        <td>{getTxnStatusBadge(txn.status)}</td>
                        <td>{renderProofCell(txn, selectedInvoice)}</td>
                        <td className="text-end text-nowrap">
                          {canApprovePendingPayment(txn) && (
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              onClick={() => openApproveModal(txn, selectedInvoice)}
                            >
                              <i className="fa fa-check me-1" />
                              Approve
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CustomModal>

      <Panel>
        <PanelHeader>
          <div className="d-flex justify-content-between align-items-center w-100">
            <span>Billing ({invoices.length})</span>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <i className="fa fa-plus me-1"></i>
              Create Invoice
            </button>
          </div>
        </PanelHeader>
        <PanelBody>
          {invoices.length === 0 ? (
            <div className="alert alert-info mb-0">
              <i className="fa fa-info-circle me-2"></i>
              No invoices yet. Create a manual invoice to bill this restaurant.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="bg-light">
                  <tr>
                    <th>Invoice #</th>
                    <th>Period</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Paid</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Payment</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => {
                    const pendingTxn = getPendingPaymentTransaction(invoice);
                    const balance = getBalanceDue(invoice);
                    return (
                      <tr key={invoice.invoiceID}>
                        <td>
                          <strong>{invoice.invoiceNumber}</strong>
                          {getBillingTypeBadge(invoice.billingType)}
                        </td>
                        <td>
                          {invoice.servicePeriodStart
                            ? `${moment(invoice.servicePeriodStart).format("MMM DD, YY")} - ${moment(invoice.servicePeriodEnd).format("MMM DD, YY")}`
                            : "-"}
                        </td>
                        <td className="text-end">
                          {invoice.currency} {parseFloat(invoice.totalAmount).toFixed(2)}
                        </td>
                        <td className="text-end text-success">
                          {invoice.currency} {parseFloat(invoice.paidAmount || 0).toFixed(2)}
                        </td>
                        <td className="text-center">{getStatusBadge(invoice.status)}</td>
                        <td className="text-center">
                          {getPaymentStatusBadge(invoice.paymentStatus)}
                          {pendingTxn && (
                            <span className="badge bg-warning ms-1">Proof Pending</span>
                          )}
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => fetchInvoiceDetails(invoice.invoiceID)}
                            title="View"
                          >
                            <i className="fa fa-eye"></i>
                          </button>
                          {balance > 0 && invoice.paymentStatus !== "Paid" && (
                            <button
                              className="btn btn-sm btn-outline-success me-1"
                              onClick={() => openRecordPayment(invoice)}
                              title="Record Payment"
                            >
                              <i className="fa fa-money-bill"></i>
                            </button>
                          )}
                          {pendingTxn && (
                            <button
                              className="btn btn-sm btn-success me-1"
                              onClick={() => openApproveModal(pendingTxn, invoice)}
                              title="Approve pending payment"
                            >
                              <i className="fa fa-check" />
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-outline-secondary me-1"
                            onClick={() => openEditInvoice(invoice)}
                            title="Edit dates"
                          >
                            <i className="fa fa-edit" />
                          </button>
                          {isInvoiceDeletable(invoice) && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteInvoice(invoice)}
                              title="Delete unpaid invoice"
                            >
                              <i className="fa fa-trash" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </PanelBody>
      </Panel>
    </div>
  );
};

export default ManualBilling;
